import { jsPDF } from "jspdf";

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 36;
const CONTENT_W = PAGE_W - MARGIN * 2;
const BOTTOM = PAGE_H - MARGIN;
const INK = [17, 17, 17];
const MUTED = [68, 68, 68];
const BORDER = [153, 153, 153];
const RULE = [187, 187, 187];

/**
 * Builds the same letter report the Print button lays out, as real text
 * and images instead of a screenshot.
 */
export async function elementToPdfBlob(element) {
  if (!element) throw new Error("Report preview is not ready");
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const title = textOf(element, ".sheet-title");
  if (title) pdf.setProperties({ title });
  const cursor = { pdf, y: MARGIN };
  drawChildren(cursor, element);
  return pdf.output("blob");
}

function drawChildren(cursor, parent) {
  const children = [...parent.children];
  for (let index = 0; index < children.length; index += 1) {
    const node = children[index];
    if (node.classList.contains("sheet-head")) drawHeader(cursor, node);
    else if (node.tagName === "H2") drawHeading(cursor, node.textContent.trim());
    else if (node.classList.contains("sheet-grid")) drawGrid(cursor, node);
    else if (node.classList.contains("sheet-photos")) drawPhotos(cursor, node);
    else if (node.classList.contains("sheet-field")) drawFieldAt(cursor, MARGIN, CONTENT_W, fieldMetrics(cursor.pdf, node, CONTENT_W));
    else if (node.classList.contains("sheet-label") && children[index + 1]?.classList.contains("sheet-sign")) {
      drawSignature(cursor, node, children[index + 1]);
      index += 1;
    }
  }
}

function drawHeader(cursor, header) {
  const { pdf } = cursor;
  const titleLines = wrap(pdf, textOf(header, ".sheet-title"), CONTENT_W, 14, "bold");
  const meta = textOf(header, ".sheet-meta");
  const sub = textOf(header, ".sheet-sub");
  const block = titleLines.length * 18 + (meta ? 16 : 0) + (sub ? 14 : 0) + 12;
  ensure(cursor, block);
  titleLines.forEach((line) => {
    write(pdf, line, MARGIN, cursor.y, 14, "bold", INK);
    cursor.y += 18;
  });
  if (meta) {
    write(pdf, meta, MARGIN, cursor.y, 10.5, "bold", INK);
    cursor.y += 16;
  }
  if (sub) {
    write(pdf, sub, MARGIN, cursor.y, 9, "normal", MUTED);
    cursor.y += 14;
  }
  pdf.setDrawColor(...RULE);
  pdf.setLineWidth(0.8);
  pdf.line(MARGIN, cursor.y, MARGIN + CONTENT_W, cursor.y);
  cursor.y += 12;
}

function drawHeading(cursor, text) {
  ensure(cursor, 22);
  write(cursor.pdf, text, MARGIN, cursor.y, 12, "bold", INK);
  cursor.y += 20;
}

function drawGrid(cursor, grid) {
  const fields = [...grid.querySelectorAll(":scope > .sheet-field")];
  const gap = 12;
  const columnWidth = (CONTENT_W - gap) / 2;
  for (let index = 0; index < fields.length; index += 2) {
    const left = fieldMetrics(cursor.pdf, fields[index], columnWidth);
    const right = fields[index + 1] ? fieldMetrics(cursor.pdf, fields[index + 1], columnWidth) : null;
    const rowHeight = Math.max(left.blockHeight, right ? right.blockHeight : 0);
    ensure(cursor, rowHeight);
    const top = cursor.y;
    paintField(cursor.pdf, MARGIN, top, columnWidth, left);
    if (right) paintField(cursor.pdf, MARGIN + columnWidth + gap, top, columnWidth, right);
    cursor.y += rowHeight;
  }
}

function drawPhotos(cursor, container) {
  const cards = [...container.children];
  const gap = 12;
  const columnWidth = (CONTENT_W - gap) / 2;
  for (let index = 0; index < cards.length; index += 2) {
    const left = cardMetrics(cursor.pdf, cards[index], columnWidth);
    const right = cards[index + 1] ? cardMetrics(cursor.pdf, cards[index + 1], columnWidth) : null;
    const rowHeight = Math.max(left.height, right ? right.height : 0);
    ensure(cursor, rowHeight);
    const top = cursor.y;
    paintCard(cursor.pdf, MARGIN, top, columnWidth, left);
    if (right) paintCard(cursor.pdf, MARGIN + columnWidth + gap, top, columnWidth, right);
    cursor.y += rowHeight + 8;
  }
}

function drawSignature(cursor, labelNode, signNode) {
  const image = signNode.querySelector("img");
  const fitted = image ? fitImage(image, 240, 72) : null;
  const block = 14 + 72 + 10;
  ensure(cursor, block);
  write(cursor.pdf, labelNode.textContent.trim(), MARGIN, cursor.y, 9, "bold", MUTED);
  cursor.y += 14;
  const boxTop = cursor.y;
  if (fitted && image.src) {
    try {
      cursor.pdf.addImage(image.src, imageFormat(image.src), MARGIN, boxTop, fitted.w, fitted.h);
    } catch {
      /* a broken signature still leaves the sign-off line */
    }
  }
  cursor.y = boxTop + 72;
  cursor.pdf.setDrawColor(0, 0, 0);
  cursor.pdf.setLineWidth(0.8);
  cursor.pdf.line(MARGIN, cursor.y, MARGIN + CONTENT_W, cursor.y);
  cursor.y += 10;
}

function drawFieldAt(cursor, x, width, metrics) {
  ensure(cursor, metrics.blockHeight);
  paintField(cursor.pdf, x, cursor.y, width, metrics);
  cursor.y += metrics.blockHeight;
}

function fieldMetrics(pdf, field, width) {
  const label = field.querySelector(".sheet-label")?.textContent?.trim() || "";
  const valueNode = field.querySelector(".sheet-value");
  const tall = Boolean(valueNode?.classList.contains("tall"));
  const innerWidth = Math.max(20, width - 10);
  const lines = wrap(pdf, valueNode?.textContent || "", innerWidth, 10.5, "normal");
  const lineHeight = 13;
  const boxHeight = Math.max(tall ? 40 : 18, lines.length * lineHeight + 6);
  return {
    label,
    lines,
    boxHeight,
    blockHeight: (label ? 13 : 0) + boxHeight + 8,
  };
}

function paintField(pdf, x, top, width, metrics) {
  let y = top;
  if (metrics.label) {
    write(pdf, metrics.label, x, y, 9, "bold", MUTED);
    y += 13;
  }
  pdf.setDrawColor(...BORDER);
  pdf.setLineWidth(0.7);
  pdf.roundedRect(x, y, width, metrics.boxHeight, 3, 3);
  metrics.lines.forEach((line, index) => {
    write(pdf, line, x + 5, y + 3 + index * 13, 10.5, "normal", INK);
  });
}

function cardMetrics(pdf, card, width) {
  const label = card.querySelector(":scope > .sheet-label")?.textContent?.trim() || "";
  const imageNode = card.querySelector(":scope > img");
  const image = imageNode ? fitImage(imageNode, width, 2.3 * 72) : null;
  const fields = [...card.querySelectorAll(":scope > .sheet-field")].map((field) => fieldMetrics(pdf, field, width));
  let height = label ? 14 : 0;
  if (image) height += image.h + 6;
  fields.forEach((field) => {
    height += field.blockHeight;
  });
  return { label, imageNode, image, fields, height };
}

function paintCard(pdf, x, top, width, card) {
  let y = top;
  if (card.label) {
    write(pdf, card.label, x, y, 9, "bold", MUTED);
    y += 14;
  }
  if (card.image && card.imageNode?.src) {
    const imageX = x + (width - card.image.w) / 2;
    try {
      pdf.addImage(card.imageNode.src, imageFormat(card.imageNode.src), imageX, y, card.image.w, card.image.h);
    } catch {
      /* skip a photo that cannot be embedded */
    }
    y += card.image.h + 6;
  }
  card.fields.forEach((field) => {
    paintField(pdf, x, y, width, field);
    y += field.blockHeight;
  });
}

function ensure(cursor, height) {
  const pageRoom = BOTTOM - MARGIN;
  if (height <= pageRoom && BOTTOM - cursor.y < height) {
    cursor.pdf.addPage();
    cursor.y = MARGIN;
  }
}

function write(pdf, text, x, top, size, style, color) {
  pdf.setFont("helvetica", style);
  pdf.setFontSize(size);
  pdf.setTextColor(...color);
  pdf.text(String(text || ""), x, top + size * 0.8);
}

function wrap(pdf, text, width, size, style) {
  const raw = String(text || "").replace(/\u00a0/g, " ").replace(/\r/g, "");
  if (!raw.trim()) return [];
  pdf.setFont("helvetica", style);
  pdf.setFontSize(size);
  return raw.split("\n").flatMap((line) => {
    const parts = pdf.splitTextToSize(line.length ? line : " ", Math.max(20, width));
    return parts.length ? parts : [" "];
  });
}

function textOf(root, selector) {
  return root.querySelector(selector)?.textContent?.trim() || "";
}

function fitImage(image, maxWidth, maxHeight) {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (!width || !height) return null;
  const scale = Math.min(maxWidth / width, maxHeight / height);
  return { w: width * scale, h: height * scale };
}

function imageFormat(src) {
  return /^data:image\/png/i.test(src) ? "PNG" : "JPEG";
}

export function pdfFileName(name, id) {
  const safe = String(name || "Report").replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "Report";
  const short = String(id || "").replace(/-/g, "").slice(0, 8);
  return short ? `${safe} (${short}).pdf` : `${safe}.pdf`;
}
